import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { OwnerLayout } from '../../components/Layout';
import { CheckCircle, ArrowRight, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollStatus(sessionId, 0);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 6) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sid}`);
      const data = res.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch (err) {
      console.error('Error polling status:', err);
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    }
  };

  return (
    <OwnerLayout>
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="glass-card p-10 rounded-3xl text-center max-w-md w-full">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-edupaws-navy mx-auto mb-6 animate-spin" />
              <h1 data-testid="payment-checking" className="font-heading text-2xl font-bold text-edupaws-navy mb-3">
                Processing Payment...
              </h1>
              <p className="text-gray-500 font-body">
                Please wait while we confirm your payment. (Attempt {attempts + 1}/6)
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 glass-sage rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-edupaws-sage" />
              </div>
              <h1 data-testid="payment-success" className="font-heading text-3xl font-bold text-edupaws-navy mb-3">
                Payment Successful!
              </h1>
              <p className="text-gray-500 font-body mb-8">
                Your training series has been unlocked. Time to start learning!
              </p>
              <Link
                to="/owner/series"
                data-testid="go-to-training-btn"
                className="inline-flex items-center gap-2 btn-primary font-heading"
              >
                Go to Training
                <ArrowRight className="w-5 h-5" />
              </Link>
            </>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 data-testid="payment-error" className="font-heading text-2xl font-bold text-edupaws-navy mb-3">
                {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
              </h1>
              <p className="text-gray-500 font-body mb-8">
                {status === 'timeout' 
                  ? 'We couldn\'t confirm your payment yet. Please check your email for confirmation.' 
                  : 'Something went wrong. Please try again or contact support.'}
              </p>
              <Link
                to="/owner/series"
                className="inline-flex items-center gap-2 btn-primary font-heading"
              >
                Back to Training
                <ArrowRight className="w-5 h-5" />
              </Link>
            </>
          )}
        </div>
      </div>
    </OwnerLayout>
  );
}
